import { Dialog, DialogContent, Typography, Divider, Box, IconButton, useTheme, Drawer, CircularProgress, Button } from '@mui/material'
import { useAssignmentStore } from "../../store/assignment.store"
import noPermissionImg from '../../assets/no-permission.svg'
import Download from "@mui/icons-material/Download"
import { useSearchParams } from 'react-router-dom'
import { Icons, attachmentThumbnails, convertToCDNLink, isIOS, formatDate, extractAssignmentFileDetailsFromMinioLocationOrKey } from "../../utils/utilities"
import { toast } from "react-toastify"
import PropTypes from 'prop-types'
import { OpenInNew, WarningAmberRounded } from '@mui/icons-material'
import api from '../../service/api'
import { useState } from 'react'
import DocumentViewer from './DocumentViewer'
import ConfirmDialog from './ConfirmDialog'
import { useUserStore } from '../../store/user.store'

const extentionsToNameMap = {
    'pdf': 'PDF',
    'docx': 'Document',
    'xslx': 'Spreadsheet',
    'jpg': 'Image',
    'jpeg': 'Image',
    'png': 'Image'
}

/**
 * AssignmentViewer component displays assignment details, attachments, and allows actions such as download, preview, and delete.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback to close the viewer dialog
 * @param {Object} props.selectedValue - The selected assignment object to display
 * @param {Boolean} props.open - Whether the dialog is open
 * @param {Boolean} props.loaded - Whether the assignment data is loaded
 * @param {Function} [props.onAfterDelete] - Optional callback after assignment deletion
 * @returns {JSX.Element} Assignment details dialog with file actions
 */
const AssignmentViewer = ( { onClose, selectedValue, open, loaded = true, onAfterDelete } ) => {

    const [previewFile, setPreviewFile] = useState( null )
    const [deleting, setDeleting] = useState( false )
    const [downloadingFile, setDownloadingFile] = useState( null )
    const [previewingFileName, setPreviewingFileName] = useState( null )
    const [confirmDialogStatus, setConfirmDialogStatus] = useState( false )

    const AssignmentStore = useAssignmentStore()
    const UserStore = useUserStore()

    const { palette, border } = useTheme()
    const [searchParams] = useSearchParams( { searchText: '', selectedAssignmentId: null, open: false } )
    const selectedAssignmentId = searchParams.get( 'selectedAssignmentId' )

    const handleClose = () => {
        onClose( selectedValue )
    }

    const handleListItemClick = async ( value ) => {
        try {
            setDownloadingFile( value )
            const { groups: fileDetails } = extractAssignmentFileDetailsFromMinioLocationOrKey( value )

            await toast.promise( AssignmentStore.downloadAssignment( value, fileDetails.fullFileName ), { success: `${decodeURIComponent( fileDetails.fullFileName )} downloaded successfully`, pending: `Downloading ${decodeURIComponent( fileDetails.fullFileName )}...` } )
        } catch ( err ) {
            console.log( err )
            toast( `Failed to download the attachment!` )
        } finally {
            setDownloadingFile( null )
        }
    }

    const deleteAssignment = async () => {
        setDeleting( true )
        let payload = { "assignment_id": selectedValue.assignment_id }
        if ( await AssignmentStore.deleteAssignment( payload, true ) ) {
            setConfirmDialogStatus( false )
            onClose( selectedValue )
        } if ( onAfterDelete )
            onAfterDelete( selectedValue )
        setDeleting( false )
    }

    const viewAttachment = async ( value ) => {
        try {
            const { data: { data } } = await api.downloadAssignment( { sourceUrl: decodeURIComponent( value ), preSignedUrl: true }, true )
            const { groups: fileDetails } = extractAssignmentFileDetailsFromMinioLocationOrKey( value )
            setPreviewingFileName( decodeURIComponent( fileDetails.fullFileName ) )
            setPreviewFile( convertToCDNLink( data ) )
        } catch ( err ) {
            console.log( err )
            toast( `An error occurred while viewing the submission!` )
        }
    }

    return (
        <Dialog maxWidth="lg" onClose={handleClose} aria-labelledby="simple - dialog - title" open={open}>
            {loaded && selectedValue && confirmDialogStatus && <ConfirmDialog secondaryConfirmationText='remove-submissions-and-delete' enableSecondaryConfirmation waiting={deleting} status={confirmDialogStatus} confirmAction={deleteAssignment} cancelAction={() => setConfirmDialogStatus( false )} message={<p> Assignment <u><strong><em>{selectedValue.assignment_name}</em></strong></u> will be deleted permanently. </p>} actionName="Yes, Delete it" />
            }
            < Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} slotProps={{
                paper: {
                    sx: {
                        height: "calc(100% - 50px)"
                    }
                }
            }} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant='h6'>{previewingFileName}</Typography>
                        <Box display="flex" alignItems="center" gap="10px" >
                            <IconButton onClick={() => setPreviewFile( null )}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} height="300px">
                        <DocumentViewer type={previewingFileName?.split( "." ).pop()} setFile={setPreviewFile} loadingText={`Loading ${previewingFileName}...`} file={previewFile} />
                    </Box>
                </Box>
            </Drawer >
            {loaded && selectedValue && !confirmDialogStatus && <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" padding={{ sm: "20px", xs: "10px" }} >
                    <Box>
                        <Typography variant="h6">{selectedValue.assignment_name}</Typography>
                        <Typography variant="subtitle2">Due Date: {new Date( selectedValue.assignment_due_date ).toLocaleDateString( "en-IN", { day: "2-digit", month: "2-digit", year: "numeric" } ).replace( /\//g, "-" )}</Typography>
                    </Box>
                    <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                </Box>
                <Divider />
                <DialogContent sx={{ marginBottom: "20px" }}>
                    {['admin', 'subadmin'].includes( UserStore.getUser?.user_role?.toLowerCase() ) && <Button onClick={() => setConfirmDialogStatus( true )} sx={{ textTransform: "capitalize", marginBottom: "20px" }} startIcon={Icons.default.DeleteIcon} color="errorMessage" variant='contained' disableElevation>Delete</Button>}
                    <Typography variant="subtitle1" fontWeight="bold" >Created On</Typography>
                    <Typography variant="subtitle2" fontSize="12px">{formatDate( selectedValue.assignment_created_at, { month: "short" } )}</Typography>

                    <Typography marginTop="20px" variant="subtitle1" fontWeight="bold" >Details</Typography>
                    <Typography variant="body1">{selectedValue.assignment_details}</Typography>

                    {/* <Typography variant="subtitle1" marginTop="20px" fontWeight="bold" >Attachments</Typography> */}
                    {selectedValue.assignment_file_paths && selectedValue.assignment_file_paths.length > 0 && <Box marginTop="20px" display="flex" gap="10px" flexDirection="column">
                        {selectedValue.assignment_file_paths.map( ( item, index ) => {
                            const { groups: fileDetails } = extractAssignmentFileDetailsFromMinioLocationOrKey( item )
                            return (
                                <Box gap="10px" flexWrap="wrap" color="secondary.main" key={`item-${index}`} width="fit-content" display="flex" alignItems="center" justifyContent="space-between" borderRadius="40px" bgcolor={palette.secondary.light + "22"} border={`1px solid ${palette.secondary.main}`} padding={{ sm: "5px 10px", xs: "5px" }} sx={{ cursor: "pointer" }}>
                                    <Box maxWidth={{ md: "inital", xs: "90%" }} display="flex" overflow="hidden" gap="5px" alignItems="center">
                                        <Box marginTop="5px">
                                            <img style={{ objectFit: "fit" }} width="35px" src={attachmentThumbnails[fileDetails?.fileextention?.toLowerCase()] || attachmentThumbnails["default"]} alt={item.split( "/" ).pop().replace( /%\d+/g, " " )} />
                                        </Box>
                                        <Box overflow="hidden">
                                            <Typography title={decodeURIComponent( fileDetails?.fullFileName )} noWrap color="secondary" variant="subtitle2">{decodeURIComponent( fileDetails?.fullFileName )}</Typography>
                                            <Typography variant="body2" color="secondary" fontSize="10px">{extentionsToNameMap[fileDetails?.fileextention?.toLowerCase()] || ""}</Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <IconButton disabled={item === downloadingFile} onClick={() => handleListItemClick( item )} size="small">{item === downloadingFile ? <CircularProgress size={20} /> : <Download color='secondary' fontSize='small' />}</IconButton>
                                        {!isIOS() && <IconButton onClick={() => viewAttachment( item )} size="small"><OpenInNew color='secondary' fontSize='small' /></IconButton>}
                                    </Box>
                                </Box>
                            )
                        } )}
                    </Box>}

                </DialogContent>
            </Box>}
            {
                loaded && !selectedValue && <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" padding={{ sm: "20px", xs: "10px" }} borderBottom="1px solid #d3d3d3">
                        <Box display="flex" gap="20px" alignItems="center">
                            <WarningAmberRounded />
                            <Typography variant="h6">Access denied</Typography>
                        </Box>
                        <IconButton sx={{ color: "white" }} onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                    </Box>
                    <Box padding={{ sm: "20px", xs: "10px" }} display="flex" flexDirection="column" gap="20px" alignItems="center">
                        <img width="200px" src={noPermissionImg} alt="No permissions" />
                        <Typography variant='subtitle2' fontWeight="normal" textAlign="center">You doesn't have the permission to view the assignment with ID <em><strong>{selectedAssignmentId}</strong></em> or this assignment has already been deleted. </Typography>
                    </Box>
                </Box>
            }
        </Dialog >
    )
}

AssignmentViewer.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
}

export default AssignmentViewer